import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  User,
  initializeAuth,
} from "firebase/auth";
// @ts-ignore
import { getReactNativePersistence } from "firebase/auth";
import { getFirestore, doc, setDoc, getDoc } from "firebase/firestore";
import {
  getAnalytics,
  isSupported as isAnalyticsSupported,
  Analytics,
  logEvent,
  setUserProperties,
} from "firebase/analytics";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createAnalyticsBuffer } from "./utils/analyticsBuffer";
import { initAppCheck } from "./lib/appCheck";

const firebaseConfig = {
  apiKey: "AIzaSyAvTvWYo_Twy6dTjcKqXYU9N_nR6n7Povo",
  authDomain: "meldpot-67df7.firebaseapp.com",
  projectId: "meldpot-67df7",
  storageBucket: "meldpot-67df7.firebasestorage.app",
  messagingSenderId: "132257275880",
  appId: "1:132257275880:web:45607a6867e1a453124e5d",
  measurementId: "G-MNR3VQDZTN",
};

const app = initializeApp(firebaseConfig);

// App Check (#126): initialize before Auth/Firestore make any requests so their
// calls carry an App Check token. Web: invisible reCAPTCHA Enterprise
// (src/lib/appCheck.ts); iOS/Android: App Attest / Play Integrity via the
// native SDK (src/lib/appCheck.native.ts).
initAppCheck(app);

const auth =
  Platform.OS === "web"
    ? getAuth(app)
    : initializeAuth(app, {
        persistence: getReactNativePersistence(AsyncStorage),
      });
const db = getFirestore(app);

// Initialize Firebase Analytics for web only and export the analytics instance.
// Use isSupported to ensure it only runs in compatible browsers/environments.
let analytics: Analytics | null = null;

// Init is async, so events fired during startup (anonymous_sign_in, the first
// screen_view) would land before `analytics` exists and be dropped — losing the
// very events a sign-up funnel starts from. Buffer them and replay on init.
const analyticsBuffer = createAnalyticsBuffer();

async function initAnalyticsIfSupported() {
  try {
    const supported = await isAnalyticsSupported();
    if (supported && typeof window !== "undefined") {
      analytics = getAnalytics(app);
      analyticsBuffer.attach((eventName, eventData) => {
        logEvent(analytics!, eventName, eventData);
      });
      // Optionally: log that analytics was initialized
      console.log("Firebase Analytics initialized");
    } else {
      // Native (isSupported() is false without IndexedDB) has no analytics
      // client yet — drop the buffer rather than hold events forever.
      analyticsBuffer.disable();
    }
  } catch (err) {
    // Non-fatal: analytics simply won't be available
    console.warn(
      "Firebase Analytics not supported or failed to initialize:",
      err,
    );
    analytics = null;
    analyticsBuffer.disable();
  }
}

// Start async init but don't block module consumers
initAnalyticsIfSupported();

/**
 * Log a custom event to Firebase Analytics
 * @param eventName - Name of the event to log
 * @param eventData - Optional data associated with the event
 */
export const logAnalyticsEvent = (
  eventName: string,
  eventData?: Record<string, any>,
): void => {
  try {
    analyticsBuffer.log(eventName, eventData);
  } catch (err) {
    console.warn(`Failed to log analytics event: ${eventName}`, err);
  }
};

/**
 * Set user properties for analytics
 * @param properties - User properties to set
 */
export const setAnalyticsUserProperties = (
  properties: Record<string, string>,
): void => {
  if (analytics) {
    try {
      setUserProperties(analytics, properties);
      console.log("Analytics user properties set", properties);
    } catch (err) {
      console.warn("Failed to set analytics user properties", err);
    }
  }
};

// Anonymous sign-in function
export const signInAnonymous = async (): Promise<
  { success: true; user: User } | { success: false; error: string }
> => {
  try {
    const result = await signInAnonymously(auth);
    console.log("Anonymous sign-in successful:", result.user.uid);
    return { success: true, user: result.user };
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred";
    console.error("Anonymous sign-in error:", error);
    return { success: false, error: errorMessage };
  }
};

// Save user name
export const saveUserName = async (
  uid: string,
  name: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log("Saving name for UID:", uid, "Name:", name);
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, { name, updatedAt: new Date() }, { merge: true });
    console.log("Name saved successfully for UID:", uid);
    return { success: true };
  } catch (error) {
    console.error("Error saving user name:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

// Save mapping between Firebase UID and Agora UID
export const saveAgoraUIDMapping = async (
  firebaseUID: string,
  agoraUID: string,
  name: string,
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log("Saving Agora UID mapping:", { firebaseUID, agoraUID, name });

    // Save name by Firebase UID
    const userRef = doc(db, "users", firebaseUID);
    await setDoc(userRef, { name, updatedAt: new Date() }, { merge: true });

    // Save mapping Agora UID -> Firebase UID
    const agoraRef = doc(db, "agora_mapping", agoraUID);
    await setDoc(
      agoraRef,
      { firebaseUID, name, updatedAt: new Date() },
      { merge: true },
    );

    console.log("Agora UID mapping saved successfully");
    return { success: true };
  } catch (error) {
    console.error("Error saving Agora UID mapping:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
};

// Get user name
export const getUserName = async (uid: string): Promise<string | null> => {
  try {
    console.log("Getting name for UID:", uid);
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    if (userDoc.exists()) {
      const userName = userDoc.data()?.name || null;
      console.log("Found name for UID:", uid, "Name:", userName);
      return userName;
    }
    console.log("No document found for UID:", uid);
    return null;
  } catch (error) {
    console.error("Error getting user name:", error);
    return null;
  }
};

export const getUserProfile = async (
  uid: string,
): Promise<{
  name: string | null;
  admin: boolean;
  banned: boolean;
  testAccount: boolean;
}> => {
  try {
    const userRef = doc(db, "users", uid);
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      return { name: null, admin: false, banned: false, testAccount: false };
    }

    const data = userDoc.data();
    return {
      name: data.name || null,
      admin: data.admin === true,
      banned: data.banned === true,
      testAccount: data.testAccount === true,
    };
  } catch (error) {
    console.error("Error getting user profile:", error);
    return { name: null, admin: false, banned: false, testAccount: false };
  }
};

// Get user name by Agora UID
export const getUserNameByAgoraUID = async (
  agoraUID: string,
): Promise<string | null> => {
  try {
    console.log("Getting name for Agora UID:", agoraUID);
    const agoraRef = doc(db, "agora_mapping", agoraUID);
    const agoraDoc = await getDoc(agoraRef);
    if (agoraDoc.exists()) {
      const userName = agoraDoc.data()?.name || null;
      console.log("Found name for Agora UID:", agoraUID, "Name:", userName);
      return userName;
    }
    console.log("No Agora mapping found for UID:", agoraUID);
    return null;
  } catch (error) {
    console.error("Error getting user name by Agora UID:", error);
    return null;
  }
};

// Check authentication state
export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

export { auth, db, analytics };
export default app;
